import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import collectionApi, { FieldError } from '../../../../services/collectionApi';
import { CollectionAgreement, withLocalIds } from './collectionAgreementModel';
import { CollectionPolicy, ReceivingAccountKind } from './collectionModel';
import { withAccountKind } from './agreementAccountRules';
import { syncWithOperation } from './collectionAgreementSuggestion';
import { reportCollectionError } from './collectionErrors';
import { AgreementSaveResult } from './submitWithAgreement';
import { agreementDraftErrors } from './agreementDraftErrors';

const useOperationCollectionAgreement = (
    agreementId: string | null | undefined,
    suggestion: () => CollectionAgreement,
    ready: boolean,
    accountKind: ReceivingAccountKind,
) => {
    const [draft, setDraft] = useState<CollectionAgreement | null>(null);
    const [policies, setPolicies] = useState<CollectionPolicy[]>([]);
    const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        collectionApi.listPolicies({ size: 500 })
            .then(policyPage => { if (active) setPolicies(policyPage.data || []); })
            .catch(error => reportCollectionError(error, 'Não foi possível carregar as políticas de cobrança.'));
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!ready || draft) return undefined;
        if (!agreementId) {
            setDraft(suggestion());
            return undefined;
        }
        let active = true;
        collectionApi.getAgreement(agreementId)
            .then(response => { if (active) setDraft(syncWithOperation(withLocalIds(response.data), suggestion())); })
            .catch(error => reportCollectionError(error, 'Não foi possível carregar o acordo de cobrança.'));
        return () => { active = false; };
    }, [ready, agreementId, draft, suggestion]);

    useEffect(() => {
        if (draft && draft.receivingAccountKind !== accountKind) setDraft(withAccountKind(draft, accountKind));
    }, [draft, accountKind]);

    useEffect(() => {
        setDraft(current => (current ? syncWithOperation(current, suggestion()) : current));
    }, [suggestion]);

    const save = useCallback(async (): Promise<AgreementSaveResult> => {
        if (!draft) return { saved: true, id: agreementId ?? null };
        const localErrors = agreementDraftErrors(draft);
        setFieldErrors(localErrors);
        if (localErrors.length) {
            setFailed(true);
            toast.error(`Acordo de cobrança: ${localErrors.map(error => error.message).join(' ')} A operação não foi salva.`);
            return { saved: false, id: null };
        }
        try {
            const { data } = await collectionApi.saveAgreement({ ...draft, id: draft.id || agreementId || '' });
            setDraft(withLocalIds(data));
            setFailed(false);
            return { saved: true, id: data.id };
        } catch (error) {
            setFailed(true);
            reportCollectionError(error, 'O acordo de cobrança não foi salvo; a operação também não.', setFieldErrors);
            return { saved: false, id: null };
        }
    }, [draft, agreementId]);

    return { draft, setDraft, policies, fieldErrors, failed, save };
};

export default useOperationCollectionAgreement;
